import React, { useState, useCallback, useMemo, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, Table, Modal, message, Tree } from "antd";
import dayjs from "dayjs";

import { Users } from "graphql/generated/graphql";
import { useGetPropertyByIdQuery } from "graphql/queries/get-property-by-id.generated";
import { useDeleteShopProductByIdMutation } from "graphql/mutations/delete-shop-product-by-id.generated";

import { MediaType, UserRoleValue } from "constants/field-values";
import routeNames from "constants/route-names";

import MediaPlayer from "components/media-player/media-player";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import { useSelectedProperty } from "state/reactive-vars/selected-property-var";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import PrivateLink from "components/private-link/private-link";
import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";
import StructuredLocation from "components/structured-location/structured-location";
import { canRoleRemove } from "utils/permissions-management";
import { getPropertyAddress } from "utils/helpers";
import { labelsFromRelationships } from "utils/location-hierarchy";
import { useUpdatePropertyByIdMutation } from "graphql/mutations/update-property-by-id.generated";
import { useDeleteUserPropertiesMutation } from "graphql/mutations/delete-user-properties.generated";
import { useUpdateInstalledProductByIdMutation } from "graphql/mutations/update-installed-product-by-id.generated";

const DocumentsColumns = [
	{
		title: "NAME",
		render: (field, record, index) => {
			if (record.name) {
				return `${record.name}`;
			}

			if (record.description) {
				return `${record.description}`;
			}

			return ``;
		},
		key: `NAME`
	},
	{
		title: "TYPE",
		dataIndex: "type",
		key: `TYPE`
	},
	{
		title: "UPLOAD DATE",
		render: (field, record, index) => {
			if (record.created_at) {
				return `${dayjs(record.created_at).format("MM/DD/YYYY")}`;
			}

			return ``;
		},
		key: `UPLOAD DATE`
	},
	{
		title: "",
		key: "actions",
		render: (_, record) => {
			const handleEditClick = (e: React.MouseEvent) => {
				e.stopPropagation();
			};

			const handleDeleteClick = (e: React.MouseEvent) => {
				e.stopPropagation();
			};

			return (
				<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
					<img
						alt={`edit-attachment-icon`}
						src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
						className="selected-file-icon disabled"
						onClick={handleEditClick}
					/>

					<img
						alt={`remove-attachment-icon`}
						src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
						className="selected-file-icon disabled"
						onClick={handleDeleteClick}
					/>
				</div>
			);
		}
	}
];

const PropertyDetails: React.FC = () => {
	const params = useParams<{ propertyId: string }>();
	const navigate = useNavigate();
	const { updateSelectedProperty } = useSelectedProperty();
	const [usersData, setUsersData] = useState<Array<Users>>([]);
	const [propertyData, setPropertyData] = useState<any>(null);
	const [documentsData, setDocumentsData] = useState<Array<any>>([]);
	const [installedProductsData, setInstalledProductsData] = useState<Array<any>>([]);
	const [shopProductsData, setShopProductsData] = useState<Array<any>>([]);

	const [fileViewer, setFileViewer] = useState<{
		type: MediaType | null;
		src: string;
	}>({ type: null, src: "" });

	const { loggedUserValue } = useLoggedUser();

	const isCurrentUserOrganization = useMemo(() => {
		return (
			loggedUserValue?.organization_id === propertyData?.organization_id ||
			loggedUserValue.role === UserRoleValue.admin_user
		);
	}, [loggedUserValue, propertyData]);

	const hierarchyTreeData = useMemo(() => {
		if (!propertyData?.buildings || propertyData.buildings.length === 0) return [];

		const renderNodeTitle = (title: string, details: { label: string; value: any }[]) => {
			const validDetails = details.filter(d => d.value !== undefined && d.value !== null && d.value !== "");
			if (validDetails.length === 0) return <span>{title}</span>;

			return (
				<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
					<span style={{ fontWeight: 500 }}>{title}</span>
					<span style={{ color: "#8c8c8c", fontSize: "12px" }}>
						{validDetails.map(d => `${d.label}: ${d.value}`).join(" | ")}
					</span>
				</div>
			);
		};

		return propertyData.buildings.map((building: any) => ({
			title: renderNodeTitle(building.name, [{ label: "Order", value: building.sort_order }]),
			key: `building-${building.id}`,
			selectable: false,
			children: building.floors?.map((floor: any) => ({
				title: renderNodeTitle(floor.name, [
					{ label: "Level", value: floor.level_number },
					{ label: "Order", value: floor.sort_order }
				]),
				key: `floor-${floor.id}`,
				selectable: false,
				children: floor.rooms?.map((room: any) => ({
					title: renderNodeTitle(room.name, [
						{ label: "Type", value: room.room_type },
						{ label: "Inspectable", value: room.is_inspectable ? "Yes" : "No" },
						{ label: "Order", value: room.sort_order }
					]),
					key: `room-${room.id}`,
					selectable: false,
					isLeaf: true
				}))
			}))
		}));
	}, [propertyData?.buildings]);

	const handleOnCompleteDeleteShopProduct = useCallback((data: any) => {
		console.log("Complete Delete Shop Product Error: ", data);
	}, []);

	const handleOnErrorDeleteShopProduct = useCallback((error: ApolloError) => {
		console.log("Delete Shop Product Error: ", error);
	}, []);

	const [deleteShopProductByIdMutation, { loading: deleteShopProductLoading }] = useDeleteShopProductByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorDeleteShopProduct,
		onCompleted: handleOnCompleteDeleteShopProduct,
		refetchQueries: ["getPropertyById"]
	});

	const handleDeleteShopProduct = useCallback(
		async (id: string) => {
			try {
				Modal.confirm({
					title: "Delete Shop Device",
					content: "Are you sure you want to remove this device from the shop? This action cannot be undone.",
					okText: "Delete",
					cancelText: "Keep",
					okButtonProps: {
						style: { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" }
					},
					onOk: async () => {
						await deleteShopProductByIdMutation({
							variables: {
								id
							}
						});
						// Success notification
						Modal.success({
							content: "Device has been successfully removed from the shop"
						});
					}
				});
			} catch (error) {
				Modal.error({
					title: "Unable to Delete Device",
					content:
						"There was a problem removing this device. Please try again or contact support if the issue persists."
				});
				console.error("Error deleting shop device:", error);
			}
		},
		[deleteShopProductByIdMutation]
	);

	const ShopProductsColumns = useMemo(() => {
		return [
			{
				title: "DEVICE",
				render: (field, record, index) => {
					if (record.product && record.product.name) {
						return `${record.product.name}`;
					}

					return ``;
				},
				key: `PRODUCT`
			},
			{
				title: "RETAIL PRICE",
				render: (field, record, index) => {
					if (record.product && record.product.retail_price) {
						return `${record.product.retail_price}`;
					}

					return ``;
				},
				key: `RETAIL PRICE`
			},
			{
				title: "EXTENDED PRICE",
				render: (field, record, index) => {
					if (record.product && record.product.extended_price) {
						return `${record.product.extended_price}`;
					}

					return ``;
				},
				key: `EXTENDED PRICE`
			},
			{
				title: "CREATION DATE",
				render: (field, record, index) => {
					if (record.created_at) {
						return `${dayjs(record.created_at).format("MM/DD/YYYY")}`;
					}

					return ``;
				},
				key: `CREATION DATE`
			},
			{
				title: "",
				key: "actions",
				render: (_, record) => {
					const requiredRoles = [UserRoleValue.admin_user, UserRoleValue.vendor_manager, UserRoleValue.vendor_employee];

					const path = `${routeNames.ADMIN_DEVICES_EDIT.replace(":deviceId", record.product_id)}`;

					const handleEditClick = (e: React.MouseEvent) => {
						e.stopPropagation();
					};

					const handleDeleteClick = (e: React.MouseEvent) => {
						e.stopPropagation();
						handleDeleteShopProduct(record.id);
					};

					return (
						<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
							<PrivateLink path={path} requiredRoles={requiredRoles} onClick={handleEditClick}>
								<img
									alt={`edit-attachment-icon`}
									src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
									className="selected-file-icon"
								/>
							</PrivateLink>
							<img
								alt={`remove-attachment-icon`}
								src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
								className="selected-file-icon"
								onClick={handleDeleteClick}
							/>
						</div>
					);
				}
			}
		];
	}, [handleDeleteShopProduct]);

	const handleOnErrorGetPropertyById = useCallback((error: ApolloError) => {
		console.log("Get Property By Id Error: ", error);
	}, []);

	const handleOnCompleteGetPropertyById = useCallback(data => {
		const properties_by_pk = data.properties_by_pk[0];
		if (!properties_by_pk) {
			message.error("Property not found.");
			navigate(routeNames.ADMIN_PROPERTIES);
			return;
		}

		const { property_documents, property_users, installed_products, shop_products } = properties_by_pk || {};

		const users = property_users.reduce((result, element) => {
			if (element.user) {
				result.push(element.user);
			}
			return result;
		}, []);

		setUsersData(users);
		setDocumentsData(property_documents);
		setInstalledProductsData(installed_products);
		setShopProductsData(shop_products);
		setPropertyData(properties_by_pk);
	}, []);

	const { loading: loadingPropertyById, refetch: refetchProperty } = useGetPropertyByIdQuery({
		skip: !params.propertyId,
		variables: {
			id: params.propertyId,
			is_not_deleted: true
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetPropertyById,
		onCompleted: handleOnCompleteGetPropertyById
	});

	const [deleteUserProperties] = useDeleteUserPropertiesMutation({
		onCompleted: () => {
			message.success("User removed successfully.");
			refetchProperty();
		},
		onError: (error: ApolloError) => {
			message.error("Failed to remove user.");
			console.log("Update User By Id Error: ", error);
		}
	});

	const UsersColumns = useMemo(
		() => [
			{
				title: "NAME",
				render: (field, record, index) => {
					return `${record.first_name} ${record.last_name}`;
				},
				key: `NAME`
			},
			{
				title: "PHONE",
				dataIndex: "phone_number",
				key: `PHONE`
			},
			{
				title: "EMAIL",
				dataIndex: "email",
				key: `EMAIL`
			},
			{
				title: "TITLE",
				dataIndex: "title",
				key: `TITLE`
			},
			{
				title: "",
				key: "actions",
				render: (_, record) => {
					const { id } = record;

					const requiredRoles = [
						UserRoleValue.admin_user,
						UserRoleValue.vendor_manager,
						UserRoleValue.customer_manager
					];

					const path = `${routeNames.ADMIN_USER_EDIT.replace(":userId", id)}`;
					const canBeRemoved = canRoleRemove(loggedUserValue.role as UserRoleValue, record.role as UserRoleValue);

					const handleEditClick = (e: React.MouseEvent) => {
						e.stopPropagation();
					};

					const handleRemoveUserClick = () => {
						if (!canBeRemoved) {
							message.error("You do not have permission to remove this user.");
							return;
						}

						deleteUserProperties({
							variables: {
								where: {
									user_id: { _eq: id },
									property_id: { _eq: params.propertyId }
								}
							}
						});
					};

					return (
						<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
							{isCurrentUserOrganization && (
								<>
									<PrivateLink path={path} requiredRoles={requiredRoles} onClick={handleEditClick}>
										<img
											alt={`edit-attachment-icon`}
											src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
											className="selected-file-icon"
										/>
									</PrivateLink>
									<RemovePopconfirm
										title="Remove User"
										description={
											<>
												<span>Are you sure you want to remove this user</span>
												<br />
												<span>from this property?</span>
											</>
										}
										handleDeleteClick={handleRemoveUserClick}
									/>
								</>
							)}
						</div>
					);
				}
			}
		],
		[]
	);

	const [updateInstalledDevice] = useUpdateInstalledProductByIdMutation({
		onCompleted: () => {
			message.success("Installed device updated successfully.");
			refetchProperty();
		},
		onError: (error: ApolloError) => {
			message.error("Failed to update installed device.");
			console.log("Update Installed Device By Id Error: ", error);
		}
	});

	const InstalledProductsColumns = useMemo(
		() => [
			{
				title: "DEVICE",
				render: (field, record, index) => {
					if (record.product && record.product.name) {
						return `${record.product.name}`;
					}

					return ``;
				},
				key: `PRODUCT`
			},
			{
				title: "INSTALLATION DATE",
				render: (field, record, index) => {
					if (record.installation_date) {
						return `${dayjs(record.installation_date).format("MM/DD/YYYY")}`;
					}

					return ``;
				},
				key: `INSTALLATION DATE`
			},
			{
				title: "EXPIRATION",
				render: (field, record, index) => {
					if (record.warranty_expiration_date) {
						return `${dayjs(record.warranty_expiration_date).format("MM/DD/YYYY")}`;
					}

					return ``;
				},
				key: `EXPIRATION`
			},
			{
				title: "LOCATION",
				render: (field, record, index) => (
					<>
						<StructuredLocation labels={labelsFromRelationships(record)} />
						{record?.location && record.location.length > 0 ? record.location : ""}
					</>
				),
				key: `LOCATION`
			},
			{
				title: "",
				key: "actions",
				render: (_, record) => {
					const requiredRoles = [UserRoleValue.admin_user, UserRoleValue.vendor_manager, UserRoleValue.vendor_employee];

					const path = `${routeNames.ADMIN_INSTALLED_DEVICE_EDIT.replace(":installedDevId", record.id)}`;

					const handleEditClick = (e: React.MouseEvent) => {
						e.stopPropagation();
					};

					const handleDeleteClick = (e: React.MouseEvent) => {
						e.stopPropagation();

						updateInstalledDevice({
							variables: {
								id: record.id,
								set: {
									deleted_at: new Date(),
									deleted_by: loggedUserValue.id
								}
							}
						});
					};

					return (
						<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
							<PrivateLink path={path} requiredRoles={requiredRoles} onClick={handleEditClick}>
								<img
									alt={`edit-attachment-icon`}
									src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
									className={"selected-file-icon"}
								/>
							</PrivateLink>
							<RemovePopconfirm
								title="Delete Installed Device"
								description={
									<>
										<span>Are you sure you want to delete this installed device?</span>
										<br />
										<span>This action cannot be undone.</span>
									</>
								}
								handleDeleteClick={handleDeleteClick}
							/>
						</div>
					);
				}
			}
		],
		[]
	);

	const handleOnClickUser = useCallback(record => {
		const { id } = record;
		const path = `${routeNames.ADMIN_USER_DETAILS.replace(":userId", id)}`;

		navigate(path);
	}, []);

	const handleOnClickEditProperty = useCallback(() => {
		if (params.propertyId) {
			const path = `${routeNames.ADMIN_PROPERTY_EDIT.replace(":propertyId", params.propertyId)}`;

			navigate(path);
		}
	}, [params.propertyId]);

	const handleOnClickInstalledProduct = useCallback(record => {
		const { id } = record;

		const path = `${routeNames.ADMIN_INSTALLED_DEVICES_DETAILS.replace(":installedDevId", id)}`;

		navigate(path);
	}, []);

	const handleOnClickShopProduct = useCallback(record => {
		const { id } = record;

		const path = `${routeNames.ADMIN_SHOP_PRODUCTS_DETAILS.replace(":shopProductId", id)}`;

		navigate(path);
	}, []);

	const handleOnClickDocumentRow = useCallback(record => {
		const { audio_link, document_link, photo_link, video_link } = record;

		if (audio_link) {
			setFileViewer({ type: MediaType.Audio, src: audio_link });
			return;
		}

		if (document_link) {
			setFileViewer({
				type: MediaType.Document,
				src: document_link
			});
			return;
		}

		if (photo_link) {
			setFileViewer({ type: MediaType.Image, src: photo_link });
			return;
		}

		if (video_link) {
			setFileViewer({ type: MediaType.Video, src: video_link });
			return;
		}
	}, []);

	const handleOnClickAddInstalledProduct = useCallback(() => {
		const query = "?propertyId=" + params.propertyId;
		navigate(routeNames.ADMIN_INSTALLED_DEVICE_ADD + query);
	}, [propertyData, params.propertyId, updateSelectedProperty]);

	const handleOnClickAddShopProduct = useCallback(() => {
		if (propertyData) {
			updateSelectedProperty({
				selectedPropertyId: params.propertyId,
				...propertyData
			});
		} else {
			updateSelectedProperty({
				selectedPropertyId: params.propertyId
			});
		}
		navigate(routeNames.ADMIN_SHOP_PRODUCT_ADD);
	}, [propertyData, params.propertyId, updateSelectedProperty]);

	const [updatedPropertyById] = useUpdatePropertyByIdMutation({
		onCompleted: () => {
			message.success("Property deleted successfully");
			navigate(routeNames.ADMIN_PROPERTIES);
		},
		onError: error => {
			console.error("Error updating property:", error);
		}
	});

	const handleDelete = useCallback(() => {
		const activeUsersCount = usersData.filter(user => user.id !== loggedUserValue.id).length;
		// If users or properties exist, show toast error
		if (activeUsersCount || installedProductsData.length) {
			// Show error toast here
			message.error("Cannot remove properties with existing users or devices.");
			return;
		}

		updatedPropertyById({
			variables: {
				id: params.propertyId!,
				set: {
					deleted_at: new Date(),
					deleted_by: loggedUserValue.id
				}
			}
		});
	}, [usersData, installedProductsData]);

	const isLoading = loadingPropertyById || deleteShopProductLoading;

	return (
		<div className="property-details-screen-container">
			{propertyData ? (
				<div className="organization-d-row">
					<Breadcrumb
						className="products-breadcrumbs"
						items={[
							{
								title: "Properties",
								onClick: () => {
									navigate(routeNames.ADMIN_PROPERTIES);
								}
							},
							{
								title: propertyData.name
							}
						]}
					/>

					{loggedUserValue.role === UserRoleValue.customer_manager ? null : propertyData?.warranty_expiration_date ? (
						<span className="warranty-expiration">
							{`Warranty  Valid  until ${dayjs(propertyData.warranty_expiration_date).format("MM/DD/YYYY")}`}
						</span>
					) : null}
				</div>
			) : null}
			<div className="organization-d-container">
				<img
					src={propertyData?.photo_link || `${process.env.PUBLIC_URL}/assets/images/icons/property-placeholder.svg`}
					className={propertyData?.photo_link ? "organization-d-photo" : "organization-d-photo placeholder"}
				/>
				<div className="organization-d-info-container">
					<div className="organization-d-row">
						<span className="organization-d-name">{propertyData?.name}</span>
						<div>
							{isCurrentUserOrganization && (
								<>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className="selected-file-icon"
										onClick={handleOnClickEditProperty}
									/>
									<RemovePopconfirm
										title="Delete Property"
										description="Are you sure to delete this property?"
										handleDeleteClick={handleDelete}
									/>
								</>
							)}
						</div>
					</div>
					<div className="organization-d-table-row">
						<div className="organization-d-table-column">
							<span className="organization-d-table-column-title">{"ID"}</span>
							<span className="organization-d-table-column-data">{propertyData?.id}</span>
						</div>
						<div className="organization-d-table-column">
							<span className="organization-d-table-column-title">{"ADDRESS"}</span>
							<span className="organization-d-table-column-data">
								{getPropertyAddress(propertyData?.address, propertyData?.standardized_address)}
							</span>
						</div>
						<div className="organization-d-table-column">
							<span className="organization-d-table-column-title">{"ORGANIZATION"}</span>
							<span className="organization-d-table-column-data">{propertyData?.organization?.name}</span>
						</div>
					</div>
				</div>
			</div>
			<div className="organization-d-table-container">
				<div className="organization-d-row">
					<span className="organization-d-name">{"Users"}</span>
				</div>
				<Table
					dataSource={usersData}
					columns={UsersColumns}
					loading={loadingPropertyById}
					rowKey={record => record.id}
					onRow={(record, rowIndex) => {
						return {
							onClick: event => {
								handleOnClickUser(record);
							}
						};
					}}
				/>
			</div>

			{loggedUserValue.role === UserRoleValue.customer_manager ? null : (
				<>
					<div className="organization-d-table-container">
						<div className="organization-d-row">
							<span className="organization-d-name">{"Resources"}</span>
						</div>
						<Table
							dataSource={documentsData}
							columns={DocumentsColumns}
							loading={loadingPropertyById}
							rowKey={record => record.id}
							onRow={(record, rowIndex) => {
								return {
									onClick: event => {
										handleOnClickDocumentRow(record);
									}
								};
							}}
						/>
					</div>
					<div className="organization-d-table-container">
						<div className="organization-d-row">
							<span className="organization-d-name">{"Installed Devices"}</span>
							{[UserRoleValue.admin_user, UserRoleValue.vendor_manager, UserRoleValue.vendor_employee].includes(
								(loggedUserValue.role || "") as UserRoleValue
							) ? (
								<img
									alt={`edit-attachment-icon`}
									src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`}
									className="organization-plus-icon"
									onClick={handleOnClickAddInstalledProduct}
								/>
							) : null}
						</div>
						<Table
							dataSource={installedProductsData}
							columns={InstalledProductsColumns}
							loading={loadingPropertyById}
							rowKey={record => record.id}
							onRow={(record, rowIndex) => {
								return {
									onClick: event => {
										handleOnClickInstalledProduct(record);
									}
								};
							}}
						/>
					</div>

					<div className="organization-d-table-container">
						<div className="organization-d-row">
							<span className="organization-d-name">{"Shop Products"}</span>
							{[UserRoleValue.admin_user, UserRoleValue.vendor_manager, UserRoleValue.vendor_employee].includes(
								(loggedUserValue.role || "") as UserRoleValue
							) ? (
								<img
									alt={`edit-attachment-icon`}
									src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`}
									className="organization-plus-icon"
									onClick={handleOnClickAddShopProduct}
								/>
							) : null}
						</div>
						<Table
							dataSource={shopProductsData}
							columns={ShopProductsColumns}
							loading={loadingPropertyById}
							rowKey={record => record.id}
							onRow={(record, rowIndex) => {
								return {
									onClick: event => {
										handleOnClickShopProduct(record);
									}
								};
							}}
						/>
					</div>
				</>
			)}

			{hierarchyTreeData.length > 0 && (
				<div className="organization-d-table-container">
					<div className="organization-d-row" style={{ marginBottom: "16px" }}>
						<span className="organization-d-name">{"Spatial Hierarchy"}</span>
					</div>
					<div style={{ padding: "0 24px 24px" }}>
						<Tree treeData={hierarchyTreeData} defaultExpandAll />
					</div>
				</div>
			)}

			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default PropertyDetails;
